/** Settings: what this server accepts, and the policy it accepts it under.
 *
 * The form is drawn from the metadata the admin API sends, not from a list of
 * fields written here. Every knob arrives with its label, its description, the
 * group it belongs to and the kind of control it wants, so a setting added in
 * `src/lib/settings.ts` and described in `src/routes/admin.ts` appears on this
 * screen with the right control and the right words, and this file does not
 * change. The grouping and the order are still the design's: the capabilities
 * card on the left, policy and housekeeping in one card on the right, the
 * providers and the service key in another, and any group the design did not
 * anticipate in a card of its own after them — because a setting with no
 * control is worse than a card the mock did not draw.
 *
 * Nothing is written until Apply. That is not caution for its own sake: the
 * per-type toggles are the server's half of the logger's opt-in, and turning
 * one off removes the type from `GET /v1/capabilities` and makes its endpoints
 * answer `403 type_disabled` for every installation at once. So the `<pre>`
 * under the toggles previews that document from the *pending* state, and an
 * admin reads what the loggers will read before committing to it. The
 * preview's field list comes from what this server actually publishes today,
 * with the values re-read from the staged settings, so it stays a preview of
 * the server's document rather than this screen's idea of one.
 *
 * Apply is one action for the whole page, and it is offered wherever the
 * change was made: a bar appears at the foot of the viewport the moment
 * anything is staged, naming the keys and carrying Apply and Discard. The
 * design drew the button inside the capabilities card, and it is still there;
 * on its own it left a change to the crossing spread, in the card on the
 * other side of the page, with a row that said "unsaved" and no visible way
 * to save it.
 *
 * The service key is rotated, never written. The plaintext exists for exactly
 * one reply and is shown here once, the way a token is: there is no second
 * chance to read it, the old key stops working the instant the new one is
 * issued, and both of those facts are said out loud before the button is
 * pressed.
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { AdminHeader } from '@/components/admin/AdminHeader';
import {
  Card,
  CopyButton,
  ErrorNote,
  Kicker,
  LoadingPanel,
  Page,
  Segmented,
  Toggle,
} from '@/components/ui';
import {
  adminSettings,
  getMeta,
  putAdminSettings,
  readCapabilities,
  rotateServiceKey,
} from '@/lib/api';
import { bytes, count, plural, shortDate } from '@/lib/format';
import type {
  AdminSettings as SettingsReply,
  CapabilityType,
  RotatedServiceKey,
  ServerSettings,
  SettingItem,
  SettingKind,
  TypeHints,
} from '@/lib/types';

type SettingValue = boolean | number | string;

/** The group whose settings are the capabilities card's type rows. */
const CAPABILITY_GROUP = 'capabilities';
/** The design's "policy & gate" card, in this order. */
const POLICY_GROUPS = ['policy', 'housekeeping'] as const;
/** The design's "providers & keys" card, in this order. */
const KEY_GROUPS = ['providers', 'security'] as const;

const BYTES_PER_MIB = 1024 * 1024;

/** A provider's toggle offers nothing until its OAuth app is configured, and
 *  that half cannot be seen from in here — so the row says what it is. */
const PROVIDER_SECRETS: Record<string, string> = {
  'providers.github.enabled': 'needs GITHUB_CLIENT_ID and GITHUB_CLIENT_SECRET on the Worker as well',
  'providers.discord.enabled':
    'needs DISCORD_CLIENT_ID and DISCORD_CLIENT_SECRET on the Worker as well',
  'providers.google.enabled': 'needs GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET on the Worker as well',
};

/** The word after a number input. A `count` has no unit; the rest do. */
const UNIT: Partial<Record<SettingKind, string>> = {
  days: 'd',
  seconds: 's',
  metres: 'm',
  bytes: 'MiB',
};

const PANEL: CSSProperties = {
  background: 'var(--color-surface)',
  borderRadius: 'var(--radius-md)',
  boxShadow: 'var(--shadow-sm)',
};

/** The fading 1px rule the design puts between the capability rows. */
const TOP_RULE: CSSProperties = {
  background:
    'linear-gradient(to right, transparent, var(--color-divider) 48px, var(--color-divider) calc(100% - 48px), transparent) no-repeat top / 100% 1px',
};

function asBoolean(value: SettingValue | null): boolean {
  return value === true;
}

function asNumber(value: SettingValue | null): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function asString(value: SettingValue | null): string {
  return typeof value === 'string' ? value : '';
}

// ── the capabilities preview ──────────────────────────────────────────────

/**
 * `GET /v1/capabilities` as it would answer if Apply were pressed now.
 *
 * Which fields a type publishes is the server's decision, so the key list is
 * taken from the document this deployment is serving. Only where a field has
 * a setting behind it is the value replaced by the staged one — that is the
 * whole point of the preview. A type the server has switched off publishes
 * nothing today, so its field list falls back to the limits the admin API
 * sends, minus the booleans, which are policy the server keeps to itself
 * rather than hints a logger reads.
 */
function previewDocument(
  types: readonly CapabilityType[],
  byKey: ReadonlyMap<string, SettingItem>,
  staged: (key: string) => SettingValue | null,
  published: Record<string, TypeHints | undefined>,
  server: string,
  version: string,
): Record<string, unknown> {
  const out: Record<string, TypeHints> = {};

  for (const type of types) {
    const enabledKey = `types.${type.name}.enabled`;
    const enabled = byKey.has(enabledKey) ? asBoolean(staged(enabledKey)) : type.enabled;
    if (!enabled) continue;

    const live = published[type.name];
    const fields = live
      ? Object.keys(live)
      : ['scope', ...Object.keys(type.limits).filter((key) => typeof type.limits[key] !== 'boolean')];

    const hints: TypeHints = {};
    for (const field of fields) {
      if (field === 'scope') {
        hints[field] = type.scope;
        continue;
      }
      const settingKey = `types.${type.name}.${field}`;
      if (byKey.has(settingKey)) {
        const value = staged(settingKey);
        if (value !== null) hints[field] = value;
        continue;
      }
      const fallback = live?.[field] ?? type.limits[field];
      if (fallback !== undefined) hints[field] = fallback;
    }
    out[type.name] = hints;
  }

  return { server, version, types: out };
}

// ── one row of a settings card ────────────────────────────────────────────

interface ApplyBarProps {
  /** The keys staged and not yet written. */
  changed: string[];
  saving: boolean;
  loading: boolean;
  /** What the last Apply wrote, until something else is staged. */
  saved: string[] | null;
  error: unknown;
  onApply: () => void;
  onDiscard: () => void;
}

/**
 * The page's one Apply, kept in view.
 *
 * Sticky at the foot of the viewport rather than fixed, so it sits inside the
 * page's measure and scrolls away with the end of the content instead of
 * covering the footer. It appears when something is staged and stays until
 * the outcome has been read: a change that was applied says which keys, and
 * one the server refused says why, in the same place the button was.
 */
function ApplyBar({ changed, saving, loading, saved, error, onApply, onDiscard }: ApplyBarProps) {
  const dirty = changed.length > 0;
  if (!dirty && !saving && saved === null && !error) return null;

  return (
    <div
      role="region"
      aria-label="Unsaved settings"
      style={{
        position: 'sticky',
        bottom: 12,
        zIndex: 5,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexWrap: 'wrap',
        padding: '10px 14px',
        borderRadius: 'var(--radius-md)',
        background: 'var(--color-surface)',
        boxShadow: 'var(--shadow-md)',
      }}
    >
      <span style={{ fontSize: 12.5, minWidth: 0, flex: 1, textWrap: 'pretty' }}>
        {dirty ? (
          <>
            <strong style={{ fontWeight: 500 }}>{plural(changed.length, 'unsaved change')}</strong>
            <span style={{ color: 'var(--color-neutral-500)' }}>
              {' · '}
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5 }}>{changed.join(', ')}</span>
              {' · nothing is written until Apply'}
            </span>
          </>
        ) : saved ? (
          <span role="status" style={{ color: 'var(--color-ok)' }}>
            {saved.length === 0
              ? 'nothing had changed, so nothing was written'
              : `applied: ${saved.join(', ')}`}
          </span>
        ) : null}
      </span>
      {error ? (
        <div style={{ flexBasis: '100%' }}>
          <ErrorNote error={error} title="nothing was changed" compact />
        </div>
      ) : null}
      <button
        type="button"
        className="btn btn-primary"
        style={{ fontSize: 12 }}
        disabled={!dirty || saving || loading}
        onClick={onApply}
      >
        {saving ? 'Applying…' : dirty ? `Apply · ${count(changed.length)}` : 'Applied'}
      </button>
      {dirty ? (
        <button
          type="button"
          className="btn btn-secondary"
          style={{ fontSize: 12 }}
          disabled={saving}
          onClick={onDiscard}
        >
          Discard
        </button>
      ) : null}
    </div>
  );
}

interface SettingRowProps {
  item: SettingItem;
  value: SettingValue | null;
  dirty: boolean;
  onChange: (next: SettingValue) => void;
}

/** A label cell and a control cell, for the two-column grid the design uses.
 *  Returned as a fragment so both land directly in the grid. */
function SettingRow({ item, value, dirty, onChange }: SettingRowProps) {
  const id = useId();
  const toggle = item.kind === 'toggle';
  const quorum = item.key === 'policy.manual_quorum';

  const note = [
    item.description,
    // The toggle is half of it; the other half is the OAuth app's two secrets.
    PROVIDER_SECRETS[item.key] ?? '',
  ]
    .filter(Boolean)
    .join(' · ');

  const label = (
    <span style={{ minWidth: 0 }}>
      {toggle || quorum ? (
        <span>{item.label}</span>
      ) : (
        <label htmlFor={id} style={{ display: 'inline', fontSize: 'inherit', color: 'inherit' }}>
          {item.label}
        </label>
      )}
      {note ? (
        <span
          style={{
            display: 'block',
            fontSize: 11.5,
            color: 'var(--color-neutral-500)',
            textWrap: 'pretty',
          }}
        >
          {note}
        </span>
      ) : null}
      {dirty ? (
        <span style={{ display: 'block', fontSize: 11, color: 'var(--color-accent-300)' }}>
          unsaved
        </span>
      ) : null}
    </span>
  );

  let control: ReactNode;
  if (toggle) {
    control = (
      <div style={{ justifySelf: 'end' }}>
        <Toggle checked={asBoolean(value)} onChange={onChange} label={item.label} labelHidden />
      </div>
    );
  } else if (quorum) {
    const current = asNumber(value);
    const options = [2, 3].includes(current) ? [2, 3] : [current, 2, 3];
    control = (
      <div style={{ justifySelf: 'end' }}>
        <Segmented<number>
          label={item.label}
          value={current}
          onChange={(next) => onChange(next)}
          options={options.map((option) => ({ value: option, label: String(option) }))}
        />
      </div>
    );
  } else if (item.kind === 'cron') {
    control = (
      <input
        id={id}
        className="input"
        value={asString(value)}
        spellCheck={false}
        style={{
          width: 110,
          justifySelf: 'end',
          fontFamily: 'var(--font-mono)',
          fontSize: 12.5,
        }}
        onChange={(event) => onChange(event.currentTarget.value)}
      />
    );
  } else {
    const inBytes = item.kind === 'bytes';
    const shown = inBytes ? Math.round(asNumber(value) / BYTES_PER_MIB) : asNumber(value);
    const unit = UNIT[item.kind];
    control = (
      <div
        style={{ justifySelf: 'end', display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}
      >
        <input
          id={id}
          className="input tabular-nums"
          type="number"
          min={0}
          step={1}
          value={String(shown)}
          title={inBytes ? bytes(asNumber(value)) : undefined}
          style={{ width: 72, textAlign: 'right' }}
          onChange={(event) => {
            const next = Number(event.currentTarget.value);
            if (!Number.isFinite(next)) return;
            onChange(inBytes ? Math.round(next * BYTES_PER_MIB) : next);
          }}
        />
        {unit ? (
          <span style={{ fontSize: 11.5, color: 'var(--color-neutral-500)', width: 26 }}>
            {unit}
          </span>
        ) : null}
      </div>
    );
  }

  return (
    <>
      {label}
      {control}
    </>
  );
}

/** The retention line to the right of a capability row. `Toggle` sets its meta
 *  slot to `nowrap` for a two-word note; this one is a sentence, so it takes a
 *  measure of its own and wraps inside it. */
const RETENTION_NOTE: CSSProperties = {
  display: 'block',
  width: 168,
  whiteSpace: 'normal',
  textAlign: 'right',
  textWrap: 'pretty',
};

const ROW_GRID: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0,1fr) auto',
  gap: '10px 16px',
  alignItems: 'center',
  fontSize: 13.5,
};

// ── the screen ────────────────────────────────────────────────────────────

export default function AdminSettings() {
  const [data, setData] = useState<SettingsReply | null>(null);
  const [published, setPublished] = useState<Record<string, TypeHints | undefined>>({});
  const [server, setServer] = useState('this server');
  const [version, setVersion] = useState('');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const [pending, setPending] = useState<Record<string, SettingValue>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<unknown>(null);
  const [saved, setSaved] = useState<string[] | null>(null);

  const [confirmRotate, setConfirmRotate] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [rotateError, setRotateError] = useState<unknown>(null);
  const [issued, setIssued] = useState<RotatedServiceKey | null>(null);

  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  /**
   * Re-read the settings.
   *
   * `keepStaged` is for a reload that had nothing to do with the form — a key
   * rotation writes one setting of its own and must not quietly throw away
   * edits somebody has staged but not applied. What it keeps is pruned against
   * the reply, so a value that has since become the server's own stops
   * claiming to be unsaved.
   */
  const load = useCallback(async (keepStaged = false) => {
    setLoading(true);
    try {
      const reply = await adminSettings();
      if (!alive.current) return;
      setData(reply);
      setPending((previous) => {
        if (!keepStaged) return {};
        const fresh = new Map(reply.settings.map((item) => [item.key, item.value]));
        const next: Record<string, SettingValue> = {};
        for (const [key, value] of Object.entries(previous)) {
          if (fresh.has(key) && fresh.get(key) !== value) next[key] = value;
        }
        return next;
      });
      setError(null);
    } catch (thrown) {
      if (!alive.current) return;
      setError(thrown);
    } finally {
      if (alive.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // The preview needs the deployment's own name and version, and the field
  // list the live document publishes. Neither is worth an error on screen:
  // without them the preview is still a faithful picture of the toggles.
  useEffect(() => {
    void readCapabilities()
      .then((document) => {
        if (!alive.current) return;
        setPublished(document.types);
        setServer(document.server);
        setVersion(document.version);
      })
      .catch(() => undefined);
    void getMeta()
      .then((meta) => {
        if (!alive.current) return;
        setServer((current) => meta.server || current);
        setVersion((current) => meta.version || current);
      })
      .catch(() => undefined);
  }, []);

  const byKey = useMemo(() => {
    const map = new Map<string, SettingItem>();
    for (const item of data?.settings ?? []) map.set(item.key, item);
    return map;
  }, [data]);

  const staged = useCallback(
    (key: string): SettingValue | null => {
      const held = pending[key];
      if (held !== undefined) return held;
      return byKey.get(key)?.value ?? null;
    },
    [byKey, pending],
  );

  const stage = useCallback(
    (item: SettingItem, next: SettingValue) => {
      setSaved(null);
      setPending((previous) => {
        const copy = { ...previous };
        if (next === item.value) delete copy[item.key];
        else copy[item.key] = next;
        return copy;
      });
    },
    [],
  );

  const changed = Object.keys(pending);
  const dirty = changed.length > 0;

  const apply = useCallback(async () => {
    if (!dirty) return;
    setSaving(true);
    setSaveError(null);
    setSaved(null);
    try {
      // The staged map is keyed by whatever the server described; the client
      // does not hold a narrower list than the server's own.
      const written = await putAdminSettings(pending as unknown as Partial<ServerSettings>);
      if (!alive.current) return;
      setSaved(written.changed);
      await load();
      // Re-read the published document: a type just switched on publishes
      // its hints from now on, and the preview should stop guessing them.
      const document = await readCapabilities().catch(() => null);
      if (alive.current && document) setPublished(document.types);
    } catch (thrown) {
      if (alive.current) setSaveError(thrown);
    } finally {
      if (alive.current) setSaving(false);
    }
  }, [dirty, load, pending]);

  const rotate = useCallback(async () => {
    setRotating(true);
    setRotateError(null);
    try {
      const key = await rotateServiceKey();
      if (!alive.current) return;
      setIssued(key);
      setConfirmRotate(false);
      await load(true);
    } catch (thrown) {
      if (alive.current) setRotateError(thrown);
    } finally {
      if (alive.current) setRotating(false);
    }
  }, [load]);

  // ── the groups, in the design's order ──────────────────────────────────

  const items = data?.settings ?? [];
  const groups = data?.groups ?? [];

  const groupLabel = useCallback(
    (id: string) => groups.find((group) => group.id === id)?.label ?? id,
    [groups],
  );
  const groupNote = useCallback(
    (id: string) => groups.find((group) => group.id === id)?.description ?? '',
    [groups],
  );
  const inGroups = useCallback(
    (ids: readonly string[]) =>
      ids.flatMap((id) => items.filter((item) => item.group === id && item.kind !== 'hash')),
    [items],
  );

  // Anything the design did not anticipate still needs a home, and it gets
  // one card per group in the order the service listed them.
  const placed = new Set<string>([CAPABILITY_GROUP, ...POLICY_GROUPS, ...KEY_GROUPS]);
  const otherGroups: string[] = [];
  for (const item of items) {
    if (!placed.has(item.group) && !otherGroups.includes(item.group)) otherGroups.push(item.group);
  }

  const serviceKey = items.find((item) => item.kind === 'hash') ?? null;

  const preview = data
    ? JSON.stringify(
        previewDocument(data.types, byKey, staged, published, server, version || '—'),
        null,
        2,
      )
    : '';

  if (error && data === null) {
    return (
      <Page max={1300} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <AdminHeader />
        <ErrorNote
          error={error}
          title="the settings could not be loaded"
          onRetry={() => void load()}
        />
      </Page>
    );
  }

  if (!data) {
    return (
      <Page max={1300} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <AdminHeader />
        <LoadingPanel label="loading settings" />
      </Page>
    );
  }

  return (
    <Page max={1300} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <AdminHeader />

      <section
        aria-label="Server settings"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
          gap: 16,
          alignItems: 'start',
        }}
      >
        {/* ── capabilities ───────────────────────────────────────────── */}
        <div style={PANEL}>
          <div
            style={{
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'baseline',
              gap: 8,
              flexWrap: 'wrap',
            }}
          >
            <Kicker>capabilities</Kicker>
            <span
              style={{ fontSize: 11, color: 'var(--color-neutral-600)', textWrap: 'pretty' }}
            >
              {groupNote(CAPABILITY_GROUP)}
            </span>
          </div>

          {data.types.length === 0 ? (
            <div
              style={{
                padding: '12px 16px',
                fontSize: 12.5,
                color: 'var(--color-neutral-500)',
                ...TOP_RULE,
              }}
            >
              This server declares no data types, so <code>/v1/capabilities</code> answers an empty
              document and every logger stops offering anything.
            </div>
          ) : null}

          {data.types.map((type) => {
            const key = `types.${type.name}.enabled`;
            const item = byKey.get(key);
            const enabled = item ? asBoolean(staged(key)) : type.enabled;
            return (
              <div key={type.name} style={{ padding: '12px 16px', ...TOP_RULE }}>
                <Toggle
                  checked={enabled}
                  onChange={(next) => (item ? stage(item, next) : undefined)}
                  disabled={!item}
                  label={
                    <>
                      <code style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}>
                        {type.name}
                      </code>{' '}
                      <span style={{ fontSize: 11.5, color: 'var(--color-neutral-500)' }}>
                        {type.scope}
                      </span>
                    </>
                  }
                  description={item?.description}
                  state={
                    <span
                      style={{
                        color: enabled ? 'var(--color-ok)' : 'var(--color-neutral-500)',
                      }}
                    >
                      {enabled ? 'accepting' : 'disabled · 403'}
                    </span>
                  }
                  meta={
                    // The design's retention note is two words; the service
                    // writes a whole sentence. A fixed measure that wraps
                    // keeps the column from eating the description beside it.
                    <span style={RETENTION_NOTE}>retention {type.retention}</span>
                  }
                />
                {pending[key] !== undefined ? (
                  <div
                    style={{
                      marginTop: 4,
                      marginLeft: 40,
                      fontSize: 11,
                      color: 'var(--color-accent-300)',
                    }}
                  >
                    unsaved — press Apply to change what loggers are offered
                  </div>
                ) : null}
              </div>
            );
          })}

          <div
            style={{
              padding: '12px 16px',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              ...TOP_RULE,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
              <Kicker>GET /v1/capabilities · public</Kicker>
              <span style={{ fontSize: 11, color: 'var(--color-neutral-600)' }}>
                {dirty ? 'preview of the pending state' : 'live preview of what loggers see'}
              </span>
            </div>

            <pre
              aria-label="capabilities preview"
              style={{
                margin: 0,
                padding: '12px 14px',
                background: 'var(--color-bg)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--color-divider)',
                fontFamily: 'var(--font-mono)',
                fontSize: 12,
                lineHeight: 1.55,
                color: 'var(--color-neutral-300)',
                overflow: 'auto',
                maxHeight: 340,
              }}
            >
              {preview}
            </pre>

            {saveError ? (
              <ErrorNote error={saveError} title="nothing was changed" compact />
            ) : null}
            {saved ? (
              <div role="status" style={{ fontSize: 11.5, color: 'var(--color-ok)' }}>
                {saved.length === 0
                  ? 'nothing had changed, so nothing was written'
                  : `applied: ${saved.join(', ')}`}
              </div>
            ) : null}

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <button
                type="button"
                className="btn btn-primary"
                style={{ fontSize: 12 }}
                disabled={!dirty || saving || loading}
                onClick={() => void apply()}
              >
                {saving ? 'Applying…' : dirty ? `Apply · ${count(changed.length)}` : 'Apply'}
              </button>
              {loading ? (
                <span className="sr-only" role="status">
                  re-reading the settings
                </span>
              ) : null}
              {dirty ? (
                <button
                  type="button"
                  className="btn btn-secondary"
                  style={{ fontSize: 12 }}
                  disabled={saving}
                  onClick={() => {
                    setPending({});
                    setSaveError(null);
                  }}
                >
                  Discard
                </button>
              ) : null}
              <button
                type="button"
                className="btn btn-secondary"
                style={{ fontSize: 12 }}
                disabled
                title="A data type is added in the service, not from this screen."
              >
                Add a type…
              </button>
              <span
                style={{
                  fontSize: 11,
                  color: 'var(--color-neutral-600)',
                  alignSelf: 'center',
                  textWrap: 'pretty',
                }}
              >
                a new type (e.g. <code style={{ fontFamily: 'var(--font-mono)' }}>races</code>) adds
                a namespace, a scope, a toggle here and a client adapter — nothing else
              </span>
            </div>
          </div>
        </div>

        {/* ── policy, keys, and anything else ────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Card style={{ padding: '14px 16px', gap: 12 }}>
            <Kicker>policy &amp; gate</Kicker>
            <div style={ROW_GRID}>
              {inGroups(POLICY_GROUPS).map((item) => (
                <SettingRow
                  key={item.key}
                  item={item}
                  value={staged(item.key)}
                  dirty={pending[item.key] !== undefined}
                  onChange={(next) => stage(item, next)}
                />
              ))}
            </div>
          </Card>

          <Card style={{ padding: '14px 16px', gap: 12 }}>
            <Kicker>providers &amp; keys</Kicker>
            <div style={ROW_GRID}>
              {inGroups(KEY_GROUPS).map((item) => (
                <SettingRow
                  key={item.key}
                  item={item}
                  value={staged(item.key)}
                  dirty={pending[item.key] !== undefined}
                  onChange={(next) => stage(item, next)}
                />
              ))}

              {serviceKey ? (
                <>
                  <span style={{ minWidth: 0 }}>
                    {serviceKey.label}
                    <span
                      className="tabular-nums"
                      style={{
                        display: 'block',
                        fontSize: 11.5,
                        color: 'var(--color-neutral-500)',
                        textWrap: 'pretty',
                      }}
                    >
                      {serviceKey.set === false
                        ? 'not set — the merge job falls back to the deployment secret'
                        : serviceKey.updated_at
                          ? `rotated ${shortDate(serviceKey.updated_at)} · used by the merge job`
                          : 'set · used by the merge job'}
                    </span>
                  </span>
                  <div style={{ justifySelf: 'end', display: 'flex', gap: 6 }}>
                    {confirmRotate ? (
                      <>
                        <button
                          type="button"
                          className="btn btn-danger"
                          style={{ fontSize: 12 }}
                          disabled={rotating}
                          onClick={() => void rotate()}
                        >
                          {rotating ? 'Rotating…' : 'Yes, rotate'}
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          style={{ fontSize: 12 }}
                          onClick={() => setConfirmRotate(false)}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        style={{ fontSize: 12 }}
                        onClick={() => {
                          setIssued(null);
                          setRotateError(null);
                          setConfirmRotate(true);
                        }}
                      >
                        Rotate
                      </button>
                    )}
                  </div>
                </>
              ) : null}
            </div>

            {confirmRotate ? (
              <p
                style={{
                  margin: 0,
                  fontSize: 11.5,
                  color: 'var(--color-warn)',
                  textWrap: 'pretty',
                }}
              >
                The current key stops working immediately, and the new one is shown once. The merge
                job cannot report an upload's status until the repository has the new key.
              </p>
            ) : null}

            {rotateError ? (
              <ErrorNote error={rotateError} title="the key was not rotated" compact />
            ) : null}

            {issued ? (
              <Card elevation="md" style={{ gap: 8 }}>
                <Kicker>shown once</Kicker>
                <code
                  style={{
                    display: 'block',
                    padding: '10px 12px',
                    background: 'var(--color-bg)',
                    border: '1px solid var(--color-divider)',
                    borderRadius: 'var(--radius-md)',
                    fontFamily: 'var(--font-mono)',
                    fontSize: 12.5,
                    color: 'var(--color-accent-300)',
                    wordBreak: 'break-all',
                  }}
                >
                  {issued.service_key}
                </code>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <CopyButton text={issued.service_key} title="copy the service key" />
                  <span style={{ fontSize: 11, color: 'var(--color-neutral-600)' }}>
                    {issued.replaced_existing ? 'replaced the previous key' : 'first key issued'}
                  </span>
                </div>
                <p
                  className="card-body"
                  style={{ margin: 0, fontSize: 11.5, color: 'var(--color-neutral-500)' }}
                >
                  {issued.note}
                </p>
              </Card>
            ) : null}
          </Card>

          {otherGroups.map((id) => (
            <Card key={id} style={{ padding: '14px 16px', gap: 12 }}>
              <Kicker>{groupLabel(id).toLowerCase()}</Kicker>
              {groupNote(id) ? (
                <span
                  style={{
                    fontSize: 11,
                    color: 'var(--color-neutral-600)',
                    textWrap: 'pretty',
                  }}
                >
                  {groupNote(id)}
                </span>
              ) : null}
              <div style={ROW_GRID}>
                {inGroups([id]).map((item) => (
                  <SettingRow
                    key={item.key}
                    item={item}
                    value={staged(item.key)}
                    dirty={pending[item.key] !== undefined}
                    onChange={(next) => stage(item, next)}
                  />
                ))}
              </div>
            </Card>
          ))}
        </div>
      </section>

      <ApplyBar
        changed={changed}
        saving={saving}
        loading={loading}
        saved={saved}
        error={saveError}
        onApply={() => void apply()}
        onDiscard={() => {
          setPending({});
          setSaveError(null);
        }}
      />
    </Page>
  );
}
